// Stok Sayım Yönetimi (WMS) sayfası kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'wms.cycleCount';

export const wmsCycleCountGuide = (t: TFunction): GuideContent => ({
  layout: 'list',
  title: t(`${G}.title`, 'Stok Sayım Yönetimi nasıl kullanılır?'),
  intro: t(`${G}.intro`, 'Sayım görevlerini burada oluşturur, personele atar, sayım bitince fark raporunu inceleyip onaylarsınız. Sayımın kendisi el terminalindeki Stok Sayım ekranında yapılır. Onaylanan farklar raf stoğunu düzeltir. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Başlık ve Depo'),
    stats: t(`${G}.regions.stats`, 'Özet kartları'),
    filters: t(`${G}.regions.filters`, 'Durum ve arama'),
    toolbar: t(`${G}.regions.toolbar`, 'Sayım Görevi Oluştur'),
    table: t(`${G}.regions.table`, 'Görev listesi'),
    pagination: t(`${G}.regions.pagination`, 'Sayfalama'),
  },
  steps: [
    {
      region: 'stats',
      title: t(`${G}.s1.title`, '1. Depoyu seçin, kartları okuyun'),
      text: t(`${G}.s1.text`, 'Sağ üstteki "Depo" listesinden akıllı depoyu seçin. Üst sıra: Toplam Görev, Bugün Görev, Devam Eden, Onay Bekleyen. Alt sıra: Depo Doluluk Oranı, Dolu / Toplanabilir Lokasyon, Boş Lokasyon ve Rastgele Sayım Havuzu (rastgele sayımda seçilebilecek lokasyon sayısı).'),
    },
    {
      region: 'toolbar',
      title: t(`${G}.s2.title`, '2. Sayım görevi oluşturun'),
      text: t(`${G}.s2.text`, '"Sayım Görevi Oluştur" penceresinde Görev Adı zorunludur. Sayım Tipi seçin:'),
      tips: [
        t(`${G}.s2.tip1`, 'Tam Sayım: aktif, toplanabilir ve stoğu olan tüm lokasyonlar depo rotasına göre sayılır.'),
        t(`${G}.s2.tip2`, 'Bölge Sayımı: seçtiğiniz koridor ve altındaki tüm raflar sayılır; Koridor seçmek zorunludur.'),
        t(`${G}.s2.tip3`, 'Rastgele Sayım: havuzdan girdiğiniz "Lokasyon Sayısı" kadar lokasyon rastgele seçilir; havuzdan fazla girerseniz tümü seçilir. 10 lokasyonun 10\'u tutarsa doğruluk %90\'ın üstünde sayılır; 4 ve üzeri fark alarm verir.'),
        t(`${G}.s2.tip4`, 'Özel Liste (Kriter): sistem seçtiğiniz kritere göre ürün listesi çıkarır ve bu ürünler bulundukları lokasyonlarda sayılır.'),
      ],
    },
    {
      region: 'toolbar',
      title: t(`${G}.s3.title`, '3. Özel liste kriterini seçin ve önizleyin'),
      text: t(`${G}.s3.text`, 'Kriterler: Eksi stok, Düşük stok (min. altı), Çok satan (Top N ürün, son N gün), Hareketsiz (eşik gün), Yüksek değerli (Top N) ve Elle seçim (ürünleri adı, barkodu veya stok koduyla arayıp eklersiniz). "Önizle" kaç ürün ve kaç lokasyonun sayılacağını gösterir; önizleme yapılmadan ya da sayılacak lokasyon çıkmazsa görev oluşturulamaz.'),
      tips: [
        t(`${G}.s3.tip1`, 'Elle seçimde yalnız bu depoda stoğu (lokasyonu) olan ürünler listelenir.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s4.title`, '4. Görevli atayın'),
      text: t(`${G}.s4.text`, 'Durumu "Yeni" olan görevin satırındaki kişi simgesi ("Görevli Ata") görevi bir personele verir. Listede bu depodaki Stok Sayım ekranına yetkili personel çıkar; ekran tanımlı değilse önce "Stok Sayım Ayarları"ndan ekran ekleyip personel atayın. Personel sayımı el terminalinde yapar.'),
    },
    {
      region: 'table',
      title: t(`${G}.s5.title`, '5. Durumları izleyin, gerekirse iptal edin'),
      text: t(`${G}.s5.text`, 'Durumlar: Yeni, Devam, Tamamlandı (onay bekliyor), Onaylandı, İptal. "Lokasyon" sütunu sayılan / toplam lokasyonu, "Fark" sütunu farklı satır sayısını gösterir. Yeni veya Devam durumundaki görev, satırdaki iptal simgesiyle iptal edilir; onay sorulmaz.'),
      tips: [
        t(`${G}.s5.tip1`, 'Sahadaki personel sorun bildirirse sayfanın üstünde "İptal Bildirgeleri" kutusu çıkar ve sesli uyarı verilir. "Görevi İptal Et" görevi iptal eder, "Kapat" yalnız bildirimi kaldırır.'),
        t(`${G}.s5.tip2`, 'Arama kutusu görev no, görev adı ve görevli adında arar; yalnız açık sayfadaki satırlarda çalışır.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s6.title`, '6. Fark raporunu inceleyin'),
      text: t(`${G}.s6.text`, '"Tamamlandı" durumundaki görevin göz simgesi "Fark Raporu"nu açar: toplam lokasyon, lokasyon farkı, lokasyon eşleşme ve ürün satırı farkı sayıları ile FARKLAR tablosu (Lokasyon, Ürün, Sistem, Sayım, Fark). Artı fark rafta sistemdekinden fazla, eksi fark eksik ürün çıktığını gösterir. Rastgele sayımda ayrıca istatistiksel doğruluk paneli görünür.'),
    },
    {
      region: 'table',
      title: t(`${G}.s7.title`, '7. Onaylayın (stok düzelir) veya yeniden saydırın'),
      text: t(`${G}.s7.text`, '"Farkları Onayla (Stok Düzelt)" her farklı satırda o raftaki stoğu sayılan miktara eşitler ve "WMS Stok Sayım Düzeltme" stok hareketi yazar; görev "Onaylandı" olur. Bu işlemin geri alma düğmesi yoktur. "Reddet (Tekrar Say)" stoğa dokunmaz: farklı satırlar sıfırlanır, görev "Devam" durumuna döner ve yalnız o lokasyonlar yeniden sayılır.'),
      tips: [
        t(`${G}.s7.tip1`, 'Onay, raf stoğunu sayılan miktara eşitler. Sayımdan sonra o rafa ürün girdi veya çıktıysa bu hareket de üzerine yazılır; bu yüzden onayı sayım biter bitmez verin.'),
      ],
    },
  ],
});
